import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { BadgeCheck } from '@/components/ui/icons'
import { GlassSurface } from '@/components/ui/GlassSurface'
import { useTheme } from '@/lib/hooks/use-theme'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'

function concisePlace(label: string): string {
  const trimmed = label.trim()
  if (!trimmed) return ''
  return trimmed.split(',')[0]?.trim() || trimmed
}

function RoutePlace({ label, place, color }: { label: string; place: string; color: string }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
      <View style={{ width: 14, alignItems: 'center' }}>
        <View style={{ width: 8, height: 8, borderRadius: RADIUS.full, backgroundColor: color }} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}>
          {label}
        </Text>
        <Text
          numberOfLines={1}
          style={{
            marginTop: 1,
            fontFamily: FONTS.bodyBold,
            fontSize: 15,
            color: colors.textPrimary,
          }}
        >
          {place}
        </Text>
      </View>
    </View>
  )
}

/** Checkout identity for a private itinerary. It deliberately avoids an
 * unrelated editorial photo: the route, date, and locked vehicle are the
 * product the guest is paying for. */
export function TourCustomCheckoutHero({
  title,
  originLabel,
  destinationLabel,
  dateLabel,
  vehicleLabel,
  stopCount,
}: {
  title: string
  originLabel: string
  destinationLabel: string
  dateLabel: string
  vehicleLabel: string
  stopCount: number
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <GlassSurface
      testID="tours.checkout-custom-hero"
      tone="tint"
      tintColor={isDark ? '#6C4C9B' : '#E7D9FA'}
      radius={RADIUS.xl}
      contentStyle={{ padding: SPACING.lg }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
        <BadgeCheck size={18} color={COLORS.purple.DEFAULT} strokeWidth={2} />
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 13, color: colors.textPrimary }}>
          {t('tours.custom_quote.detail_eyebrow')}
        </Text>
      </View>
      <Text
        testID="checkout.vehicle-title"
        style={{
          marginTop: SPACING.sm,
          fontFamily: FONTS.bodyBold,
          fontSize: 22,
          lineHeight: 28,
          color: colors.textPrimary,
        }}
      >
        {title}
      </Text>
      <Text
        testID="checkout.trip-dates"
        style={{
          marginTop: 2,
          fontFamily: FONTS.body,
          fontSize: 15,
          lineHeight: 21,
          color: colors.textSecondary,
        }}
      >
        {dateLabel}
      </Text>
      <GlassSurface
        tone="quiet"
        radius={RADIUS.lg}
        style={{
          marginTop: SPACING.md,
        }}
        contentStyle={{
          paddingHorizontal: SPACING.md,
          paddingVertical: SPACING.sm + 2,
          gap: SPACING.sm,
        }}
      >
        <RoutePlace
          label={t('tours.custom_quote.origin')}
          place={concisePlace(originLabel)}
          color={COLORS.purple.DEFAULT}
        />
        <RoutePlace
          label={t('tours.custom_quote.destination')}
          place={concisePlace(destinationLabel)}
          color={COLORS.coral.DEFAULT}
        />
      </GlassSurface>
      <View
        style={{ marginTop: SPACING.md, flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.xs }}
      >
        <GlassSurface
          tone="regular"
          radius={RADIUS.full}
          contentStyle={{
            paddingHorizontal: SPACING.sm,
            paddingVertical: 4,
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textPrimary }}>
            {vehicleLabel}
          </Text>
        </GlassSurface>
        <GlassSurface
          tone="regular"
          radius={RADIUS.full}
          contentStyle={{
            paddingHorizontal: SPACING.sm,
            paddingVertical: 4,
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textPrimary }}>
            {t('tours.custom_quote.route_points', { count: stopCount })}
          </Text>
        </GlassSurface>
      </View>
    </GlassSurface>
  )
}
